import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import RecommendedChecks from "../components/RecommendedChecks";
import { useCompany } from "../hooks/useCompany";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Legend,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const UNASSIGNED_COLOR = "#9ca3af";
const CATEGORY_COLORS  = ["#f59e0b", "#ef4444", "#3b82f6", "#22c55e", "#a78bfa", "#f97316", "#06b6d4"];

function getTodayDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function getWeekRange() {
  const today = new Date();
  const dow = today.getDay();
  const start = new Date(today);
  start.setDate(today.getDate() - dow);
  const end = new Date(today);
  end.setDate(today.getDate() + (6 - dow));
  return {
    startDate: start.toISOString().split("T")[0],
    endDate:   end.toISOString().split("T")[0],
  };
}

function formatWeekLabel(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const CHART_RANGES = [
  { labelKey: "chart_3m",  value: 3  },
  { labelKey: "chart_6m",  value: 6  },
  { labelKey: "chart_12m", value: 12 },
];

function Dashboard({ handleSignOut }) {
  const auth = useAuth();
  const { t } = useTranslation();
  const { companies, activeCompany, switchCompany } = useCompany();

  const [employeeCount, setEmployeeCount]   = useState(0);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [weeklyCost, setWeeklyCost]         = useState(0);
  const [todayDate, setTodayDate]           = useState("");
  const [chartData, setChartData]           = useState([]);
  const [chartMonths, setChartMonths]       = useState(3);
  const [chartLoading, setChartLoading]     = useState(false);

  const [bankLinked, setBankLinked]               = useState(false);
  const [bankDisconnected, setBankDisconnected]   = useState(false);
  const [bankBalances, setBankBalances]           = useState([]);
  const [bankTransactions, setBankTransactions]   = useState([]);
  const [userAssignments, setUserAssignments]         = useState({});
  const [spendDays, setSpendDays]                     = useState(30);
  const [selectedAccount, setSelectedAccount]         = useState("all");

  // Static date — set once on mount
  useEffect(() => { setTodayDate(getTodayDate()); }, []);

  // Employee count + this-week payroll summary
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token || !activeCompany) return;
    const token = auth.user.access_token;
    const cid = activeCompany.company_id;

    const fetchEmployees = async () => {
      try {
        const res = await fetch(`${API_URL}/employees?company_id=${cid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setEmployeeCount((await res.json()).length);
      } catch (e) { console.error("Error fetching employees:", e); }
    };

    const fetchWeeklyPayroll = async () => {
      const { startDate, endDate } = getWeekRange();
      try {
        const res = await fetch(
          `${API_URL}/payroll_entries/date_range?start_date=${startDate}&end_date=${endDate}&company_id=${cid}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.status === 404) { setPayrollSummary([]); setWeeklyCost(0); return; }
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        const summary = data.reduce((acc, entry) => {
          const { employee_name, hours_worked, total_pay, is_salary, wage } = entry;
          if (!acc[employee_name]) {
            acc[employee_name] = {
              employee_name,
              hours_worked: hours_worked || 0,
              total_pay: is_salary ? Number(wage) : (total_pay || 0),
              is_salary,
            };
          } else {
            acc[employee_name].hours_worked += hours_worked || 0;
            if (!acc[employee_name].is_salary) acc[employee_name].total_pay += total_pay || 0;
          }
          return acc;
        }, {});
        const arr = Object.values(summary).sort((a, b) => a.employee_name.localeCompare(b.employee_name));
        setPayrollSummary(arr);
        setWeeklyCost(arr.reduce((s, e) => s + e.total_pay, 0));
      } catch (e) { console.error("Error fetching payroll entries:", e); }
    };

    fetchEmployees();
    fetchWeeklyPayroll();
  }, [auth.isAuthenticated, auth.user?.access_token, activeCompany]);

  // Bank — check link status and load balances on mount
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) return;
    const token = auth.user.access_token;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchBankStatus = async () => {
      try {
        const [statusRes, balRes] = await Promise.all([
          fetch(`${API_URL}/api/bank/status`, { headers }),
          fetch(`${API_URL}/api/bank/balance`, { headers }),
        ]);
        if (!statusRes.ok) return;
        const status = await statusRes.json();
        if (!status.linked) return;
        setBankLinked(true);
        if (status.disconnected) { setBankDisconnected(true); return; }
        if (balRes.ok) setBankBalances((await balRes.json()).accounts || []);
      } catch (e) { console.error("Error fetching bank status/balance:", e); }
    };

    fetchBankStatus();
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // Bank — re-fetch transactions + user assignments whenever spendDays changes (or on initial link)
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token || !bankLinked || bankDisconnected) return;
    const token = auth.user.access_token;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchTransactions = async () => {
      try {
        const [txRes, assignRes] = await Promise.all([
          fetch(`${API_URL}/api/bank/transactions?days=${Math.min(spendDays, 90)}`, { headers }),
          fetch(`${API_URL}/api/bank/transactions/categories`, { headers }),
        ]);
        if (txRes.ok) setBankTransactions((await txRes.json()).transactions || []);
        if (assignRes.ok) setUserAssignments(await assignRes.json());
      } catch (e) { console.error("Error fetching bank transactions:", e); }
    };

    fetchTransactions();
  }, [auth.isAuthenticated, auth.user?.access_token, bankLinked, bankDisconnected, spendDays]);

  // Chart data — refetch when company or range changes
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token || !activeCompany) return;
    const token = auth.user.access_token;
    const cid = activeCompany.company_id;
    setChartLoading(true);
    fetch(`${API_URL}/payroll_entries/weekly_totals?months=${chartMonths}&company_id=${cid}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setChartData(data))
      .catch(() => setChartData([]))
      .finally(() => setChartLoading(false));
  }, [auth.isAuthenticated, auth.user?.access_token, activeCompany, chartMonths]);

  const accountFilteredTxns = useMemo(() => (
    selectedAccount === "all"
      ? bankTransactions
      : bankTransactions.filter((t) => t.account_id === selectedAccount)
  ), [bankTransactions, selectedAccount]);

  const filteredBalances = useMemo(() => (
    selectedAccount === "all" ? bankBalances : bankBalances.filter((a) => a.account_id === selectedAccount)
  ), [bankBalances, selectedAccount]);

  const totalDeposits = useMemo(() => (
    accountFilteredTxns
      .filter((t) => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
  ), [accountFilteredTxns]);

  const spendingData = useMemo(() => {
    const totals = {};
    accountFilteredTxns
      .filter((t) => t.amount > 0)
      .forEach((t) => {
        const cat = userAssignments[t.transaction_id]?.category_name || "Unassigned";
        if (cat === "N/A") return;
        totals[cat] = (totals[cat] || 0) + t.amount;
      });
    const sorted = Object.entries(totals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const top5 = sorted.slice(0, 5);
    const rest = sorted.slice(5);

    const result = top5.map((entry, i) => ({
      ...entry,
      fill: entry.name === "Unassigned" ? UNASSIGNED_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));

    if (rest.length > 0) {
      result.push({
        name: "Other",
        value: rest.reduce((sum, e) => sum + e.value, 0),
        fill: "#d1d5db",
      });
    }

    return result;
  }, [accountFilteredTxns, userAssignments]);

  if (auth.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-400 text-sm">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  const fmt = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{t("dashboard_title")}</h1>
            <p className="text-xs sm:text-sm text-gray-400">{todayDate}</p>
          </div>
          <img src="./assets/images/Payclear800.png" alt="PayClear" className="h-8 sm:h-12" />
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white shadow-sm rounded-lg p-4 text-center">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">{t("stat_employees")}</p>
            <p className="text-3xl font-bold text-blue-500">{employeeCount}</p>
          </div>
          <div className="bg-white shadow-sm rounded-lg p-4 text-center">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">{t("stat_active_company")}</p>
            <p className="text-xl font-bold text-gray-700 truncate">{activeCompany?.name || "—"}</p>
          </div>
          <div className="bg-white shadow-sm rounded-lg p-4 text-center">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">{t("stat_weekly_payroll")}</p>
            <p className="text-3xl font-bold text-green-500">{fmt(weeklyCost)}</p>
          </div>
        </div>

        {/* Company quick-picker */}
        {companies.length > 1 && (
          <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-3">{t("quick_switch_title")}</p>
            <div className="flex flex-wrap gap-2">
              {companies.map((c) => (
                <button
                  key={c.company_id}
                  onClick={() => switchCompany(c.company_id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                    c.company_id === activeCompany?.company_id
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-600"
                  }`}
                >
                  {c.name}{c.is_default ? " ★" : ""}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Payroll cost chart */}
        <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-700">{t("chart_title")}</p>
            <div className="flex gap-1">
              {CHART_RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setChartMonths(r.value)}
                  className={`px-3 py-1 rounded text-xs font-medium transition ${
                    chartMonths === r.value
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {t(r.labelKey)}
                </button>
              ))}
            </div>
          </div>
          {chartLoading ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("loading")}</div>
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("chart_no_data")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="payrollGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="week"
                  tickFormatter={formatWeekLabel}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(v) => [fmt(v), t("tooltip_total_payroll")]}
                  labelFormatter={(l) => t("week_of", { date: formatWeekLabel(l) })}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fill="url(#payrollGradient)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Weekly payroll table */}
        <div className="bg-white shadow-sm rounded-lg p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">{t("weekly_breakdown_title")}</p>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-y-auto max-h-56">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <th className="px-4 py-2 text-left border border-gray-200">{t("col_employee")}</th>
                  <th className="px-4 py-2 text-left border border-gray-200">{t("col_hours")}</th>
                  <th className="px-4 py-2 text-left border border-gray-200">{t("col_total_pay")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payrollSummary.length > 0 ? payrollSummary.map((entry, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border border-gray-200">{entry.employee_name || "Unknown"}</td>
                    <td className="px-4 py-2 border border-gray-200">{entry.hours_worked.toFixed(2)}</td>
                    <td className="px-4 py-2 border border-gray-200">{fmt(entry.total_pay)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" className="px-4 py-4 text-center text-gray-400 border border-gray-200">
                      {t("no_payroll_this_week")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2 max-h-56 overflow-y-auto">
            {payrollSummary.length > 0 ? payrollSummary.map((entry, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-gray-700 truncate">{entry.employee_name || "Unknown"}</p>
                <div className="text-right ml-3 flex-shrink-0">
                  <p className="text-sm font-bold text-green-600">{fmt(entry.total_pay)}</p>
                  <p className="text-xs text-gray-400">{entry.hours_worked.toFixed(2)} hrs</p>
                </div>
              </div>
            )) : (
              <p className="text-center text-gray-400 text-sm py-4">{t("no_payroll_this_week")}</p>
            )}
          </div>
        </div>
        {/* ── Recommended Checks ── */}
        <RecommendedChecks className="mt-6" />

        {/* ── Bank sections ── */}
        {bankLinked && bankDisconnected && (
          <div className="mt-6 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm text-yellow-800 font-medium">
              Your bank connection is disconnected. Visit <a href="/BankAccount" className="underline font-semibold">Bank Account</a> to reconnect.
            </p>
          </div>
        )}
        {bankLinked && !bankDisconnected && (
          <>
            {/* Account balances + selector */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">Bank Account Balances</p>
                {/* Account selector tabs */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedAccount("all")}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                      selectedAccount === "all"
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                    }`}
                  >
                    All Accounts
                  </button>
                  {bankBalances.map((acc) => (
                    <button
                      key={acc.account_id}
                      onClick={() => setSelectedAccount(acc.account_id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        selectedAccount === acc.account_id
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                      }`}
                    >
                      {acc.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {filteredBalances.map((acc) => (
                    <div key={acc.account_id} className="bg-white shadow-sm rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-400 uppercase font-semibold mb-1 truncate">{acc.name}</p>
                      <p className="text-2xl font-bold text-blue-500">
                        ${acc.balances.available != null ? acc.balances.available.toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—"}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Available</p>
                    </div>
                  ))}
                {/* Deposits stat card */}
                <div className="bg-white shadow-sm rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Deposits ({spendDays}d)</p>
                  <p className="text-2xl font-bold text-green-500">{fmt(totalDeposits)}</p>
                  <p className="text-xs text-gray-400 mt-1">Money In</p>
                </div>
              </div>
            </div>

            {/* Spending pie + recent transactions side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

              {/* Spending breakdown pie chart */}
              <div className="bg-white shadow-sm rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">Spending Breakdown</p>
                  <select
                    value={spendDays}
                    onChange={(e) => setSpendDays(Number(e.target.value))}
                    className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </div>
                {spendingData.length === 0 ? (
                  <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No spending data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={spendingData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        outerRadius={90}
                      />
                      <Tooltip
                        formatter={(value, name) => [fmt(value), name]}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value, entry) => `${value} — ${fmt(entry.payload.value)}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Recent transactions */}
              <div className="bg-white shadow-sm rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Recent Transactions</p>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-y-auto max-h-56">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <th className="px-3 py-2 text-left border border-gray-200">Name</th>
                        <th className="px-3 py-2 text-left border border-gray-200">Amount</th>
                        <th className="px-3 py-2 text-left border border-gray-200">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {accountFilteredTxns.slice(0, 10).map((txn) => (
                        <tr key={txn.transaction_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 border border-gray-200 truncate max-w-[140px]">{txn.name}</td>
                          <td className={`px-3 py-2 border border-gray-200 font-semibold ${txn.amount < 0 ? "text-green-600" : "text-red-500"}`}>
                            {txn.amount < 0 ? "+" : "-"}${Math.abs(txn.amount).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 border border-gray-200 text-gray-400">{txn.date}</td>
                        </tr>
                      ))}
                      {accountFilteredTxns.length === 0 && (
                        <tr>
                          <td colSpan="3" className="px-3 py-4 text-center text-gray-400 border border-gray-200">No transactions</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-2 max-h-56 overflow-y-auto">
                  {accountFilteredTxns.slice(0, 10).map((txn) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <div className="min-w-0 mr-3">
                        <p className="text-sm font-medium text-gray-700 truncate">{txn.name}</p>
                        <p className="text-xs text-gray-400">{txn.date}</p>
                      </div>
                      <p className={`text-sm font-bold flex-shrink-0 ${txn.amount < 0 ? "text-green-600" : "text-red-500"}`}>
                        {txn.amount < 0 ? "+" : "-"}${Math.abs(txn.amount).toFixed(2)}
                      </p>
                    </div>
                  ))}
                  {accountFilteredTxns.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No transactions</p>
                  )}
                </div>
              </div>

            </div>
          </>
        )}

      </div>
      </Sidebar>
    </div>
  );
}

export default Dashboard;
